/**
 * Générateur de prompt système pour l'API Claude
 * Extrait les informations du JSON de flow pour créer un prompt optimisé
 */

export class ClaudePromptGenerator {
  
  /**
   * Génère un système prompt basé sur le JSON de flow
   * @param {Object} flowData - Le JSON contenant les données du flow
   * @returns {String} Le système prompt formaté pour Claude
   */
  static generateSystemPrompt(flowData) {
    if (!flowData?.flow?.blocks) {
      throw new Error('Format de données invalide : blocks manquants');
    }

    const blocks = flowData.flow.blocks;
    const mainBlocks = flowData.flow.mainblocks || {};
    
    // Extraire les informations de chaque bloc
    const blockInfos = this.extractBlockInformations(blocks);
    
    // Générer le prompt système
    const systemPrompt = this.buildSystemPrompt(blockInfos, mainBlocks);
    
    return systemPrompt;
  }

  /**
   * Extrait les informations pertinentes de chaque bloc
   * @param {Array} blocks - Liste des blocs
   * @returns {Array} Informations structurées des blocs
   */
  static extractBlockInformations(blocks) {
    return blocks.map(block => {
      const info = {
        idblock: block.idblock,
        blockname: block.blockname || 'Sans nom',
        type: block.type,
        messages: [],
        buttons: [],
        hasContent: block.flow && block.flow.length > 0
      };

      // Extraire le contenu du flow
      if (block.flow) {
        block.flow.forEach(element => {
          switch (element.type) {
            case 'text':
              if (element.message) {
                info.messages.push(element.message);
              }
              break;
              
            case 'text_button':
              if (element.message) {
                info.messages.push(element.message);
              }
              if (element.buttons) {
                element.buttons.forEach(btn => {
                  info.buttons.push({
                    id: btn.id,
                    title: btn.title
                  });
                });
              }
              break;
              
            case 'list':
              if (element.interactive?.header?.text) {
                info.messages.push(element.interactive.header.text);
              }
              if (element.interactive?.body?.text) {
                info.messages.push(element.interactive.body.text);
              }
              break;
          }
        });
      }
      
      return info;
    });
  }

  /**
   * Construit le prompt système final
   * @param {Array} blockInfos - Informations des blocs
   * @param {Object} mainBlocks - Blocs principaux
   * @returns {String} Prompt système formaté
   */
  static buildSystemPrompt(blockInfos, mainBlocks) {
    const activeBlocks = blockInfos.filter(block => block.hasContent);
    
    let prompt = `Tu es un assistant IA utilisé dans une plateforme de création de chatbot pour réorienter et répondre à des questions lorsque l'utilisateur sort du scénario prévu

`;

    // Ajouter les informations de chaque bloc
    activeBlocks.forEach(block => {
      prompt += `BLOC: ${block.blockname} (ID: ${block.idblock})
- Type: ${block.type}`;

      if (block.messages.length > 0) {
        prompt += `
- Contenu: ${block.messages.join('\n\n')}`;
      }

      if (block.buttons.length > 0) {
        prompt += `
- Boutons: ${block.buttons.map(btn => btn.title).join(', ')}`;
      }
      prompt += '\n\n';
    });

    prompt += `INSTRUCTIONS:
1. Analyse le message de l'utilisateur
2. Identifie le bloc le plus pertinent basé sur le contenu
3. Retourne UNIQUEMENT un JSON valide au format suivant:

{
  "type": "redirection",
  "id_element": null,
  "id_previous": null,
  "redirection_block": "idblock_correspondant"
}

RÈGLES:
- Si aucun bloc ne correspond clairement, utilise le bloc par défaut
- Sois précis dans la correspondance du contenu
- Priorise les correspondances exactes sur le contenu
- Ne retourne que le JSON, aucun autre texte

BLOCS SPÉCIAUX:
- Bloc d'accueil: ${mainBlocks.welcome?.[0]?.idblock || 'Non défini'}
- Bloc par défaut: ${mainBlocks.default?.[0]?.idblock || 'Non défini'}
- Bloc commerce: ${mainBlocks.commerce?.[0]?.idblock || 'Non défini'}`;

    return prompt;
  }

  /**
   * Génère une fonction de test pour valider le prompt
   * @param {Object} flowData - Données du flow
   * @returns {Function} Fonction de test
   */
  static createTestFunction(flowData) {
    const systemPrompt = this.generateSystemPrompt(flowData);
    
    return async function testClaudeRedirection(userMessage, claudeApiKey) {
      if (!claudeApiKey) {
        throw new Error('Clé API Claude requise pour les tests');
      }

      try {
        const response = await fetch('https://api.anthropic.com/v1/messages', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-api-key': claudeApiKey,
            'anthropic-version': '2023-06-01'
          },
          body: JSON.stringify({
            model: 'claude-3-sonnet-20240229',
            max_tokens: 1000,
            system: systemPrompt,
            messages: [
              {
                role: 'user',
                content: userMessage
              }
            ]
          })
        });

        if (!response.ok) {
          throw new Error(`Erreur API Claude: ${response.status}`);
        }

        const data = await response.json();
        const result = data.content[0].text;
        
        try {
          return JSON.parse(result);
        } catch (e) {
          console.error('Réponse Claude non-JSON:', result);
          throw new Error('Réponse Claude invalide');
        }
      } catch (error) {
        console.error('Erreur lors du test Claude:', error);
        throw error;
      }
    };
  }

  /**
   * Génère des exemples de test basés sur le contenu des blocs
   * @param {Object} flowData - Données du flow
   * @returns {Array} Exemples de test
   */
  static generateTestExamples(flowData) {
    const blockInfos = this.extractBlockInformations(flowData.flow.blocks);
    const examples = [];

    blockInfos.forEach(block => {
      if (block.messages.length > 0) {
        // Créer un exemple basé sur le premier message du bloc
        const firstWords = block.messages[0].split(' ').slice(0, 5).join(' ');
        examples.push({
          userMessage: firstWords,
          expectedBlock: block.idblock,
          description: `Test pour le bloc ${block.blockname}`
        });
      }

      block.buttons.forEach(button => {
        examples.push({
          userMessage: button.title,
          expectedBlock: button.id,
          description: `Test pour le bouton ${button.title}`
        });
      });
    });

    return examples;
  }
}

// Fonction utilitaire pour usage direct
export function generateClaudePrompt(flowData) {
  return ClaudePromptGenerator.generateSystemPrompt(flowData);
}

// Exemple d'utilisation
export function createClaudeIntegration(flowData, apiKey) {
  const testFunction = ClaudePromptGenerator.createTestFunction(flowData);
  const examples = ClaudePromptGenerator.generateTestExamples(flowData);
  
  return {
    systemPrompt: ClaudePromptGenerator.generateSystemPrompt(flowData),
    testFunction,
    examples,
    
    // Fonction pour tester rapidement
    async quickTest(userMessage) {
      return await testFunction(userMessage, apiKey);
    }
  };
}
